import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import type { CommerceMaterialSingleItemCreateInput } from '../../../../services/materialCommerceSheetService.ts';
import { getCategoriesWithMaterials } from '../../../../services/commerceService.ts';
import ModalOverlay from '../../../UI/ModalOverlay/ModalOverlay.tsx';
import {
  type CatalogPickOption,
  normStr,
  parseCategoriesMaterialsResponse,
  parseDecimalField,
} from './commerceMaterialsCatalogPick.ts';
import styles from './CommerceMaterialsCatalog.module.scss';

const ERR_SHEET_REJECTED = 'ADD_ITEM_SHEET_REJECTED';
const ERR_BUILD_ROW = 'ADD_ITEM_BUILD_ROW';

function friendlySaveMaterialError(err: unknown): string {
  if (isAxiosError(err)) {
    const s = err.response?.status;
    if (s === 401 || s === 403) {
      return 'Tu sesión no alcanza para completar esta acción. Volvé a iniciar sesión e intentá otra vez.';
    }
    if (s === 404) {
      return 'Algo cambió del lado del servidor. Actualizá la página e intentá de nuevo.';
    }
    if (s === 409) {
      return 'Ese material ya figura en tu listado o hay un conflicto con los datos actuales.';
    }
    if (s === 400 || s === 422) {
      return 'Los datos no pudieron aceptarse. Revisá importe y opciones e intentá de nuevo.';
    }
    if (s === 429) {
      return 'Estamos recibiendo muchas solicitudes. Esperá un momento y probá de nuevo.';
    }
    if (s != null && s >= 500) {
      return 'El servicio no está disponible en este momento. Probá más tarde.';
    }
    if (err.code === 'ERR_NETWORK' || err.response == null) {
      return 'No pudimos conectar. Revisá tu conexión a internet e intentá de nuevo.';
    }
    return 'No pudimos registrar el material. Probá de nuevo en unos instantes.';
  }
  if (err instanceof Error && err.message === ERR_SHEET_REJECTED) {
    return 'El material no pudo darse de alta con los datos enviados. Revisá precio y opciones e intentá de nuevo.';
  }
  if (err instanceof Error && err.message === ERR_BUILD_ROW) {
    return 'Faltan datos o hay un problema al preparar el alta. Revisá el formulario e intentá de nuevo.';
  }
  return 'No pudimos registrar el material. Revisá los datos e intentá de nuevo.';
}

export interface CommerceMaterialsCatalogAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  listedMaterialIds: ReadonlySet<number>;
  disableActions?: boolean;
  onAddItem: (input: CommerceMaterialSingleItemCreateInput) => Promise<void>;
}

export const CommerceMaterialsCatalogAddModal: React.FC<CommerceMaterialsCatalogAddModalProps> = ({
  isOpen,
  onClose,
  listedMaterialIds,
  disableActions = false,
  onAddItem,
}) => {
  const [addModMaterialKey, setAddModMaterialKey] = useState('');
  const [addMaterialSearch, setAddMaterialSearch] = useState('');
  const addMaterialSearchRef = useRef<HTMLInputElement>(null);
  const [addPrice, setAddPrice] = useState('');
  const [addPromotion, setAddPromotion] = useState('');
  const [addPromotionDiscount, setAddPromotionDiscount] = useState('');
  const [addUnquoted, setAddUnquoted] = useState(false);
  const [addVat, setAddVat] = useState(true);
  const [addRowError, setAddRowError] = useState('');
  const [catalogAll, setCatalogAll] = useState<CatalogPickOption[]>([]);
  const [catalogStatus, setCatalogStatus] = useState<'idle' | 'loading' | 'error' | 'ready'>('idle');
  const [catalogFetchError, setCatalogFetchError] = useState('');
  const [saveUi, setSaveUi] = useState<'form' | 'saving' | 'success'>('form');

  const resetForm = useCallback(() => {
    setSaveUi('form');
    setAddRowError('');
    setAddMaterialSearch('');
    setAddModMaterialKey('');
    setAddPrice('');
    setAddPromotion('');
    setAddPromotionDiscount('');
    setAddUnquoted(false);
    setAddVat(true);
    setCatalogAll([]);
    setCatalogStatus('idle');
    setCatalogFetchError('');
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }
    setAddRowError('');
    setAddMaterialSearch('');
    setAddModMaterialKey('');
    setAddPrice('');
    setAddPromotion('');
    setAddPromotionDiscount('');
    setAddUnquoted(false);
    setAddVat(true);
    setCatalogAll([]);
    setCatalogStatus('loading');
    setCatalogFetchError('');
    setSaveUi('form');
  }, [isOpen, resetForm]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setCatalogStatus('loading');
    setCatalogFetchError('');
    void (async () => {
      try {
        const res = await getCategoriesWithMaterials();
        if (cancelled) return;
        const items = parseCategoriesMaterialsResponse(res.data);
        setCatalogAll(items);
        setCatalogStatus('ready');
      } catch {
        if (cancelled) return;
        setCatalogAll([]);
        setCatalogStatus('error');
        setCatalogFetchError('No se pudo cargar el catálogo de materiales. Probá de nuevo más tarde.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || saveUi !== 'success') return;
    const id = window.setTimeout(() => {
      onClose();
    }, 1800);
    return () => window.clearTimeout(id);
  }, [isOpen, saveUi, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (saveUi !== 'form') return;
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, saveUi]);

  const availableCatalog = useMemo(
    () =>
      catalogAll.filter((o) => {
        const n = Number(o.materialId);
        return Number.isFinite(n) && n > 0 && !listedMaterialIds.has(n);
      }),
    [catalogAll, listedMaterialIds]
  );

  const addMaterialSearchMatches = useMemo(() => {
    if (catalogStatus !== 'ready') return [];
    const raw = addMaterialSearch.trim();
    if (raw.length < 2) return [];
    const q = normStr(raw);
    return availableCatalog
      .filter((o) => normStr(`${o.materialName} ${o.categoryName}`).includes(q))
      .slice(0, 80);
  }, [catalogStatus, availableCatalog, addMaterialSearch]);

  const addSelectedPick = useMemo((): CatalogPickOption | null => {
    if (!addModMaterialKey) return null;
    return availableCatalog.find((o) => `${o.categoryId}\t${o.materialId}` === addModMaterialKey) ?? null;
  }, [addModMaterialKey, availableCatalog]);

  if (!isOpen) {
    return null;
  }

  return (
    <ModalOverlay isOpen onClose={onClose} preventClose={saveUi !== 'form'} zIndex={80}>
      <div
        className={styles.addModalPanel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cmc-add-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.addModalHeader}>
          <h2 id="cmc-add-title" className={styles.modalTitle}>
            Agregar material al comercio
          </h2>
          <button
            type="button"
            className={styles.modalClose}
            onClick={onClose}
            aria-label="Cerrar"
            disabled={disableActions || saveUi !== 'form'}
          >
            ×
          </button>
        </div>
        {saveUi === 'form' ? (
          <>
            <div className={styles.addModalScroll}>
              <p className={`${styles.modalLead} ${styles.addModalLead}`}>
                Buscá en el catálogo Edify, elegí un resultado y completá tu oferta. Las validaciones las responde el
                servidor al guardar.
              </p>
              {catalogStatus === 'error' ? <p className={styles.modalError}>{catalogFetchError}</p> : null}
              {catalogStatus === 'ready' && availableCatalog.length === 0 ? (
                <p className={styles.modalMeta}>
                  No hay materiales disponibles para agregar (ya están en tu listado o el catálogo está vacío).
                </p>
              ) : null}
              {catalogStatus === 'loading' || (catalogStatus === 'ready' && availableCatalog.length > 0) ? (
                <div className={styles.addModalBodyStack}>
                  <div className={styles.modalField}>
                    <label className={styles.modalLabel} htmlFor="cmc-add-search">
                      Buscar material
                    </label>
                    <input
                      ref={addMaterialSearchRef}
                      id="cmc-add-search"
                      className={styles.modalInput}
                      type="search"
                      autoComplete="off"
                      placeholder="Ej.: cemento, pintura…"
                      value={addMaterialSearch}
                      onChange={(e) => setAddMaterialSearch(e.target.value)}
                      disabled={disableActions || catalogStatus === 'loading'}
                    />
                <p className={styles.addModalSearchHint}>
                  {catalogStatus === 'loading'
                    ? 'Cargando catálogo…'
                    : addSelectedPick
                      ? 'Material seleccionado. Usá Cambiar para buscar otro.'
                      : addMaterialSearch.trim().length < 2
                        ? 'Escribí al menos 2 letras para ver coincidencias.'
                        : addMaterialSearchMatches.length === 0
                          ? 'No hay coincidencias. Probá otras palabras.'
                          : `${addMaterialSearchMatches.length} resultado${addMaterialSearchMatches.length === 1 ? '' : 's'}`}
                </p>
                {catalogStatus === 'loading' ? null : addMaterialSearch.trim().length >= 2 &&
                  !addModMaterialKey &&
                  addMaterialSearchMatches.length > 0 ? (
                  <div className={styles.addModalMatchList} role="listbox" aria-label="Coincidencias de catálogo">
                    {addMaterialSearchMatches.map((o) => {
                      const key = `${o.categoryId}\t${o.materialId}`;
                      return (
                        <button
                          key={key}
                          type="button"
                          role="option"
                          aria-selected={false}
                          className={styles.addModalMatchRow}
                          onClick={() => {
                            setAddModMaterialKey(key);
                            setAddRowError('');
                          }}
                        >
                          <span className={styles.addModalMatchName}>{o.materialName}</span>
                          <span className={styles.addModalMatchCat}>{o.categoryName}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              {catalogStatus === 'ready' && addSelectedPick ? (
                <div className={styles.addModalSelected}>
                  <span className={styles.addModalSelectedLabel}>Seleccionado</span>
                  <span className={styles.addModalSelectedText}>
                    <strong>{addSelectedPick.materialName}</strong>
                    <span className={styles.modalMeta}> · {addSelectedPick.categoryName}</span>
                  </span>
                  <button
                    type="button"
                    className={styles.addModalChangePick}
                    onClick={() => {
                      setAddModMaterialKey('');
                      setAddRowError('');
                      queueMicrotask(() => addMaterialSearchRef.current?.focus());
                    }}
                  >
                    Cambiar
                  </button>
                </div>
              ) : null}
                  <div className={styles.addModalOfferBlock}>
                    <div className={styles.modalField}>
                      <label className={styles.modalLabel} htmlFor="cmc-add-price">
                        Importe
                      </label>
                      <input
                        id="cmc-add-price"
                        className={styles.modalInput}
                        value={addPrice}
                        onChange={(e) => setAddPrice(e.target.value)}
                        disabled={disableActions || catalogStatus === 'loading' || !addModMaterialKey}
                      />
                    </div>
                    <label className={styles.modalCheck}>
                      <input
                        type="checkbox"
                        checked={!addUnquoted}
                        onChange={(e) => setAddUnquoted(!e.target.checked)}
                        disabled={disableActions || catalogStatus === 'loading' || !addModMaterialKey}
                      />
                      Mostrar precio al público
                    </label>
                    <div className={styles.modalField}>
                      <label className={styles.modalLabel} htmlFor="cmc-add-promo">
                        Promoción
                      </label>
                      <input
                        id="cmc-add-promo"
                        className={styles.modalInput}
                        value={addPromotion}
                        onChange={(e) => setAddPromotion(e.target.value)}
                        disabled={disableActions || catalogStatus === 'loading' || !addModMaterialKey}
                      />
                    </div>
                    <div className={styles.modalField}>
                      <label className={styles.modalLabel} htmlFor="cmc-add-disc">
                        Descuento (%)
                      </label>
                      <input
                        id="cmc-add-disc"
                        className={styles.modalInput}
                        value={addPromotionDiscount}
                        onChange={(e) => setAddPromotionDiscount(e.target.value)}
                        disabled={disableActions || catalogStatus === 'loading' || !addModMaterialKey}
                      />
                    </div>
                    {!addUnquoted ? (
                      <label className={styles.modalCheck}>
                        <input
                          type="checkbox"
                          checked={addVat}
                          onChange={(e) => setAddVat(e.target.checked)}
                          disabled={disableActions || catalogStatus === 'loading' || !addModMaterialKey}
                        />
                        IVA incluido
                      </label>
                    ) : null}
                  </div>
                  {addRowError ? <p className={styles.modalError}>{addRowError}</p> : null}
                </div>
              ) : null}
            </div>
            <div className={`${styles.modalFooter} ${styles.addModalFooter}`}>
              <button
                type="button"
                className={styles.modalBtnSecondary}
                onClick={onClose}
                disabled={disableActions}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={styles.modalBtnPrimary}
                disabled={
                  disableActions ||
                  catalogStatus !== 'ready' ||
                  availableCatalog.length === 0 ||
                  !addModMaterialKey
                }
                onClick={async () => {
                  const pick = addSelectedPick;
                  if (!pick) {
                    setAddRowError('Elegí un material de la lista de coincidencias.');
                    return;
                  }
                  const materialId = Number(pick.materialId);
                  const userCategoryId = Number(pick.categoryId);
                  const priceValue = parseDecimalField(addPrice);
                  if (addPrice.trim().length > 0 && priceValue == null) {
                    setAddRowError('Revisá el formato del importe.');
                    return;
                  }
                  const discountValue = parseDecimalField(addPromotionDiscount);
                  setAddRowError('');
                  setSaveUi('saving');
                  try {
                    await onAddItem({
                      materialId,
                      userCategoryId,
                      rawName: pick.materialName.trim() || '—',
                      price: priceValue,
                      unquoted: addUnquoted,
                      valueAddedTax: addUnquoted ? false : addVat,
                      promotion: addPromotion.trim() ? addPromotion.trim() : null,
                      promotionDiscount: discountValue,
                    });
                    setSaveUi('success');
                  } catch (e: unknown) {
                    setSaveUi('form');
                    setAddRowError(friendlySaveMaterialError(e));
                  }
                }}
              >
                Guardar
              </button>
            </div>
          </>
        ) : (
          <div className={styles.addModalBodyReplace} role="status" aria-live="polite">
            {saveUi === 'saving' ? (
              <>
                <svg className={styles.addModalSaveSpinnerSvg} viewBox="0 0 40 40" aria-hidden>
                  <circle className={styles.addModalSaveSpinnerTrack} cx="20" cy="20" r="16" />
                  <circle className={styles.addModalSaveSpinnerArc} cx="20" cy="20" r="16" />
                </svg>
                <p className={styles.addModalSaveLead}>Guardando el material en tu listado…</p>
              </>
            ) : (
              <p className={styles.addModalSaveSuccess}>Listo. El material quedó agregado a tu catálogo.</p>
            )}
          </div>
        )}
      </div>
    </ModalOverlay>
  );
};
